import { SorterResult } from "antd/es/table/interface";
import { SortOrder } from "antd/lib/table/interface";

import parseDate from "./parseDate";
import formatAmount from "./formatAmount";
import formatTime from "./formatTime";
import renderDateTime from "./renderDateTime";
import renderDateTimeNoTimezone from "./renderDateTimeNoTimezone";
import renderDateTimeZoned from "./renderDateTimeZoned";

enum SortDirection {
  asc = "asc",
  desc = "desc",
}

export const pageToSkip = (page: number, pageSize: number): number =>
  (page - 1) * pageSize;

export type OrderByInput<T> = Partial<Record<keyof T, SortDirection | null>>;

export const sorterToOrderBy = <T>(
  sort: SorterResult<T>[]
): OrderByInput<T>[] =>
  sort
    .map(({ columnKey, order }) => {
      if (!columnKey) {
        return undefined;
      }

      return {
        [columnKey]:
          order === "descend" ? SortDirection.desc : SortDirection.asc,
      } as OrderByInput<T>;
    })
    .filter((orderBy): orderBy is OrderByInput<T> => !!orderBy);

export const getSortOrder = (
  columnKey: string,
  querySortingParams: Record<string, SortDirection>[]
): SortOrder | undefined => {
  if (!querySortingParams) {
    return undefined;
  }

  const flatParams = Object.assign({}, ...querySortingParams);

  if (flatParams[columnKey] === SortDirection.asc) {
    return "ascend";
  }

  if (flatParams[columnKey] === SortDirection.desc) {
    return "descend";
  }

  return undefined;
};

export const renderTableDateColumn =
  <RecordType>(dataIndex?: keyof RecordType) =>
  (value: unknown, record: RecordType): string =>
    renderDateTime(dataIndex ? record[dataIndex] : value);

export const renderTableDateNoTimezoneColumn =
  <RecordType>(dataIndex?: keyof RecordType) =>
  (value: unknown, record: RecordType): string =>
    renderDateTimeNoTimezone(dataIndex ? record[dataIndex] : value);

export const renderTableDateZonedColumn =
  <RecordType>(
    dataIndex?: keyof RecordType,
    timezoneDataIndex?: keyof RecordType
  ) =>
  (value: unknown, record: RecordType): string => {
    const timezone = timezoneDataIndex ? record[timezoneDataIndex] : undefined;

    return renderDateTimeZoned(
      dataIndex ? record[dataIndex] : value,
      typeof timezone === "string" ? timezone : undefined
    );
  };

export const renderTableTimeColumn =
  <RecordType>(dataIndex?: keyof RecordType) =>
  (value: unknown, record: RecordType): string => {
    const dateValue = parseDate(dataIndex ? record[dataIndex] : value);

    if (!dateValue) {
      return "-";
    }

    return formatTime(dateValue);
  };

export const renderTableAmountColumn =
  <RecordType>(dataIndex?: keyof RecordType) =>
  (value: unknown, record: RecordType): string => {
    let amountValue = dataIndex ? record[dataIndex] : value;

    if (!amountValue) {
      return "-";
    }

    if (typeof amountValue === "string") {
      amountValue = Number.parseFloat(amountValue);
    }

    if (typeof amountValue !== "number" || Number.isNaN(amountValue)) {
      throw new Error(`Invalid amount value: "${value}"`);
    }

    return formatAmount(amountValue);
  };
